import React from 'react';
import { Link } from 'react-router-dom';
import { Dataset } from 'app/data/datasets';
import { formatEntities, shortUniverse } from './coverage';
import {
  ConstituentChips,
  CoverageMeter,
  NotDocumented,
  StatusChip,
} from './ui';

export type SortKey =
  | 'name'
  | 'category'
  | 'historyStart'
  | 'entities'
  | 'universe'
  | 'status';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: SortKey;
  direction: SortDirection;
}

interface ColumnDef {
  key: SortKey;
  label: string;
  /** Numeric and date columns are right-aligned so digits line up. */
  numeric?: boolean;
}

const COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'dataset' },
  { key: 'category', label: 'category' },
  { key: 'historyStart', label: 'history start', numeric: true },
  { key: 'entities', label: 'entities', numeric: true },
  { key: 'universe', label: 'universe' },
  { key: 'status', label: 'status' },
];

/**
 * Undefined always sorts last, in both directions. A missing value is not a
 * small value — floating gaps to the top on a descending sort would imply the
 * dataset ranks worst, when in fact we simply have not published the number.
 */
export function sortDatasets(rows: Dataset[], sort: SortState): Dataset[] {
  const sign = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[sort.key];
    const bv = b[sort.key];

    if (av === undefined && bv === undefined)
      return a.name.localeCompare(b.name);
    if (av === undefined) return 1;
    if (bv === undefined) return -1;

    const primary =
      typeof av === 'number' && typeof bv === 'number'
        ? (av - bv) * sign
        : String(av).localeCompare(String(bv)) * sign;

    return primary !== 0 ? primary : a.name.localeCompare(b.name);
  });
}

/** Fallback when the table is embedded without owning sort state. */
export const DEFAULT_TABLE_SORT: SortState = { key: 'name', direction: 'asc' };

const ariaSortFor = (
  column: SortKey,
  sort: SortState,
): 'ascending' | 'descending' | 'none' =>
  sort.key !== column
    ? 'none'
    : sort.direction === 'asc'
    ? 'ascending'
    : 'descending';

export interface DatasetTableProps {
  rows: Dataset[];
  /** Current sort. Optional so the table can be embedded read-only. */
  sort?: SortState;
  /**
   * Omit to render static (non-sortable) column headers — the shape the
   * homepage embed wants, where there is no sort state to own.
   */
  onSortChange?: (key: SortKey) => void;
  /** Render at most this many rows. Undefined renders all of them. */
  limit?: number;
}

export function DatasetTable({
  rows,
  sort = DEFAULT_TABLE_SORT,
  onSortChange,
  limit,
}: DatasetTableProps) {
  const visible = limit === undefined ? rows : rows.slice(0, limit);
  const sortable = typeof onSortChange === 'function';

  return (
    // The table scrolls inside this container; the page body never scrolls sideways.
    <div className="overflow-x-auto border border-gray-800">
      <table className="w-full min-w-[900px] border-collapse text-[13px]">
        <caption className="sr-only">
          Sov.ai dataset catalogue. Use the column header buttons to sort.
        </caption>
        <thead>
          <tr className="border-b border-gray-800 bg-gray-900/40">
            {COLUMNS.map(column => {
              const active = sort.key === column.key;
              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={
                    sortable ? ariaSortFor(column.key, sort) : undefined
                  }
                  className={`px-2.5 py-1.5 font-normal ${
                    column.numeric ? 'text-right' : 'text-left'
                  }`}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => onSortChange!(column.key)}
                      className={`inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider hover:text-blue-300 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400 ${
                        active ? 'text-blue-400' : 'text-gray-400'
                      }`}
                    >
                      <span>{column.label}</span>
                      <span
                        aria-hidden="true"
                        className={active ? '' : 'text-gray-600'}
                      >
                        {active ? (sort.direction === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  ) : (
                    <span className="font-mono text-[11px] uppercase tracking-wider text-gray-400">
                      {column.label}
                    </span>
                  )}
                </th>
              );
            })}
            <th
              scope="col"
              className="px-2.5 py-1.5 text-left font-mono text-[11px] font-normal uppercase tracking-wider text-gray-400"
            >
              <span title="How many of the coverage fields are published for this dataset">
                documented
              </span>
            </th>
          </tr>
        </thead>
        <tbody className="text-gray-300">
          {visible.map(dataset => (
            <tr
              key={dataset.slug}
              className="border-b border-dashed border-gray-800/70 last:border-b-0 hover:bg-gray-900/60 focus-within:bg-gray-900/60"
            >
              <th
                scope="row"
                className="px-2.5 py-1 text-left font-normal align-top"
              >
                <Link
                  to={`/datasets/${dataset.slug}`}
                  className="text-white underline decoration-gray-700 underline-offset-2 hover:text-blue-300 hover:decoration-blue-400 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
                >
                  {dataset.name}
                </Link>
                <span className="block max-w-lg truncate text-[11px] leading-snug text-gray-400">
                  {dataset.summary}
                </span>
                {/*
                  Only the datasets whose pull was unfiltered carry real
                  constituents; the rest render nothing here rather than a
                  placeholder. See ConstituentChips.
                */}
                <ConstituentChips tickers={dataset.sampleTickers} limit={5} />
              </th>
              <td className="px-2.5 py-1 align-top font-mono text-[11px] text-gray-400">
                {dataset.category}
              </td>
              <td className="px-2.5 py-1 text-right align-top font-mono text-[11px] tabular-nums text-gray-200">
                {dataset.historyStart ?? (
                  <NotDocumented label="History start" />
                )}
              </td>
              <td className="px-2.5 py-1 text-right align-top font-mono text-[11px] tabular-nums text-gray-200">
                {dataset.entities === undefined ? (
                  <NotDocumented label="Entity count" />
                ) : (
                  formatEntities(dataset.entities)
                )}
              </td>
              <td
                className={`px-2.5 py-1 align-top font-mono text-[11px] ${
                  dataset.universe && dataset.universe.includes('private')
                    ? 'text-green-400'
                    : 'text-gray-400'
                }`}
              >
                {dataset.universe ? (
                  shortUniverse(dataset.universe)
                ) : (
                  <NotDocumented label="Universe" />
                )}
              </td>
              <td className="px-2.5 py-1 align-top">
                <StatusChip status={dataset.status} />
              </td>
              <td className="px-2.5 py-1 align-top">
                <CoverageMeter dataset={dataset} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default DatasetTable;
